import { AxiosResponse } from 'axios';
import { httpClient, qsOptions } from 'providers/clients/httpClient';
import qs from 'qs';
import { AnyRecord } from 'utility/types/cosmosTypes';
import { toTitleCase } from 'utility/functions';
import { CommonResultType, StatusOnlyResponse } from 'utility/types/dataProvider';
import { paths, components } from 'utility/types/openapi-ts-generated';

// ------------------------------------------------
// EmailReach API types
// ------------------------------------------------
export interface EmailReachAccountPayload {
  vendor_driver?: 'ConstantContact' | string;
  name?: string;
  is_default?: boolean;
  vendor_auth_data?: {
    redirect_uri?: string;
    oauth_code?: string;
    code_verifier?: string;
  };
}

export interface EmailReachAccountResponse {
  vendor_driver?: string;
  name?: string;
  vendor_name?: string;
  is_default?: boolean;
  vendor_auth_data?: {
    redirect_uri?: string;
    oauth_code?: string;
    code_verifier?: string;
  };
}

export interface EmailReachEditAccountPayload {
  name?: string;
  is_default?: boolean;
}

export type GetEmailReachAccountByIdParams = {
  accountId?: string;
};

export type GetEmailReachAccountByIdResult =
  paths['/emailreach/account/{account_id}/']['get']['responses'][200]['content']['application/json'];

export type GetEmailReachAccountsParams = {
  page?: string;
  limit?: number;
};

type BaseEmailReachAccount = components['schemas']['emailreach.account.account'];

export type EmailReachAccountType = {
  uuid: BaseEmailReachAccount['uuid'];
  name: BaseEmailReachAccount['name'];
  vendor_name: BaseEmailReachAccount['vendor_name'];
  vendor_driver: BaseEmailReachAccount['vendor_driver'];
  is_default: BaseEmailReachAccount['is_default'];
  metadata?: AnyRecord;
  email?: string;
};

export type GetEmailReachAccountsResult = {
  status?: CommonResultType['status'];
  data?: {
    total?: CommonResultType['total'];
    next?: CommonResultType['next'];
    prev?: CommonResultType['prev'];
    items?: EmailReachAccountType[];
    [key: string]: any;
  };
  [key: string]: any;
};

export type DeleteEmailReachAccountParams = {
  accountId?: string;
};

export type DeleteEmailReachAccountResponse = StatusOnlyResponse;

// backend type is incorrect "items?: null[] | undefined"
// type GetEmailReachVendorsResponseGenerated = paths['/emailreach/vendor/']['get']['responses'][200]['content']['application/json']

export type ERVendorItem = {
  name?: string;
  driver?: string;
  authentication?: {
    method?: string;
    client_id?: string;
    auth_url?: string;
    scopes?: string[];
    response_type?: string;
    credentials_needed?: string[];
  };
};

export type GetEmailReachVendorsParams = {
  page?: string;
  limit?: number;
};

export type GetEmailReachVendorsResponse = {
  status?: CommonResultType['status'];
  data?: {
    total?: CommonResultType['total'];
    next?: CommonResultType['next'];
    prev?: CommonResultType['prev'];
    items?: ERVendorItem[];
    [key: string]: any;
  };
  [key: string]: any;
};

type BaseEmailReachEditContactPayload =
  components['schemas']['emailreach.contact.contact-update'];

export type EmailReachEditContactPayload = {
  contact_id?: BaseEmailReachEditContactPayload['contact_id'];
  first_name?: BaseEmailReachEditContactPayload['first_name'];
  last_name?: BaseEmailReachEditContactPayload['last_name'];
  email_address?: BaseEmailReachEditContactPayload['email_address'];
  lists?: string[];
};

type BaseSubscribeEmailReachContactPayload =
  components['schemas']['emailreach.contact.subscribe'];

export type SubscribeEmailReachContactPayload = BaseSubscribeEmailReachContactPayload;

export type SubscribeEmailReachContactParams = {
  account_id?: string;
  contact_id?: string;
  payload?: SubscribeEmailReachContactPayload;
};

export type SubscribeEmailReachContactResponse =
  paths['/emailreach/contact/{account_id}/{contact_id}/subscribe/']['put']['responses'][200]['content']['application/json'];

export type UnsubscribeEmailReachContactParams = {
  account_id?: string;
  contact_id?: string;
};

export type SendOptInEmailParams = {
  account_id?: string;
  contact_id?: string;
  list_ids?: string[];
};

export type UnsubscribeEmailReachContactResponse =
  paths['/emailreach/contact/{account_id}/{contact_id}/unsubscribe/']['put']['responses'][200]['content']['application/json'];

type BaseEmailReachContactPayload = components['schemas']['emailreach.contact.contact'];

export type EmailReachContactPayload = {
  contact_id?: BaseEmailReachContactPayload['contact_id'];
  first_name?: BaseEmailReachContactPayload['first_name'];
  last_name?: BaseEmailReachContactPayload['last_name'];
  email_address?: BaseEmailReachContactPayload['email_address'];
  create_source?: string;
  lists?: string[];
};

export type EmailReachContactListResult =
  paths['/emailreach/contactlist/{account_id}/']['get']['responses'][200]['content']['application/json'];

export type EmailReachContactListExportParams = {
  account_id?: string;
  list_id?: string | number;
};

export type EmailReachContactListExportResponse = {
  status?: CommonResultType['status'];
  data?: {
    task_id?: string | number;
    id?: string | number;
    export_id?: string | number;
    [key: string]: any;
  };
};

export type EmailReachContactListExportDownloadParams = {
  account_id?: string;
  export_id?: string | number;
};

export type EmailReachContactListExportDownloadResponse = AxiosResponse<Blob>;

export type EmailReachContactListExportStatusResponse = {
  status: number;
};

export type CreateEmailReachAccountParams = {
  payload?: EmailReachAccountPayload;
};

export type CreateEmailReachAccountResponse = {
  status?: CommonResultType['status'];
  data?: EmailReachAccountResponse;
};

export type ReauthenticateEmailReachAccountPayload = {
  vendor_auth_data?: {
    redirect_uri?: string;
    oauth_code?: string;
    code_verifier?: string;
  };
};

export type ReauthenticateEmailReachAccountParams = {
  account_id?: string;
  payload?: ReauthenticateEmailReachAccountPayload;
};

export type ReauthenticateEmailReachAccountResponse = StatusOnlyResponse;

export type UpdateEmailReachAccountParams = {
  id?: string;
  payload?: EmailReachEditAccountPayload;
};

export type UpdateEmailReachAccountResponse = {
  status?: CommonResultType['status'];
  data?: EmailReachAccountResponse;
};

export type GetEmailReachAccountByIdResponse = {
  status?: CommonResultType['status'];
  data?: EmailReachAccountResponse;
};

export type CreateEmailReachContactParams = {
  account_id?: string;
  payload?: EmailReachContactPayload;
};

export type CreateEmailReachContactResponse =
  paths['/emailreach/contact/{account_id}/']['post']['responses'][201]['content']['application/json'];

type GetEmailReachContactListParams = {
  account_id?: string;
  page?: string | number;
  limit?: string | number;
};

// ref: components['schemas']['emailreach.contactlist.contactlist']
type BaseEmailReachContactListItem = {
  /** Unique ID of the contact list generated by the vendor */
  list_id?: string;
  /** Name of the contact list */
  title: string;
  /**
   * Format: date-time
   * The timestamp indicating when the resource was created. Follows the ISO 8601 format (YYYY-MM-DDTHH:MM:SSZ) in UTC.
   */
  created_at?: string;
  /**
   * Format: date-time
   * The timestamp indicating when the resource was last updated. Follows the ISO 8601 format (YYYY-MM-DDTHH:MM:SSZ) in UTC.
   */
  updated_at?: string;
  /** The order to display this list compared to other lists */
  priority?: number;
  /** String icon data */
  icon?: string;
  /** The frequency the list is sent out */
  frequency?: string;
  /** The description of the list */
  description?: string;
  /** Whether the list is anonymous */
  anonymous?: boolean;
  /** Whether to display the list on signup */
  display_on_signup?: boolean;
  /** Whether to hide the list */
  hide?: boolean;
  /** Use this value in outgoing emails instead of the one set with the vendor */
  from_name?: string;
  /**
   * The sender's email address to appear in the email
   * header near the from name and the subject line.
   */
  from_email?: string;
  /**  Name of the contact list */
  name: string;
  /**
   * The sender's email address to appear in the email
   * header near the from name and the subject line.
   */
  from_address?: string;
};

export type EmailReachContactListItem = {
  id?: string | number;
  uuid?: string;
  account_id?: string;
  list_id: BaseEmailReachContactListItem['list_id'];
  name: BaseEmailReachContactListItem['name'];
  created_at: BaseEmailReachContactListItem['created_at'];
  updated_at: BaseEmailReachContactListItem['updated_at'];
  from_name: BaseEmailReachContactListItem['from_name'];
  from_address: BaseEmailReachContactListItem['from_address'];
  description?: string;
  display_name?: string;
  label?: string;
};

export type GetEmailReachContactListResponse = {
  status?: CommonResultType['status'];
  data?: {
    total?: CommonResultType['total'];
    next?: CommonResultType['next'];
    prev?: CommonResultType['prev'];
    items?: EmailReachContactListItem[];
    [key: string]: any;
  };
  [key: string]: any;
};

export type UpdateEmailReachContactParams = {
  account_id?: string;
  contact_id?: string;
  payload?: EmailReachEditContactPayload;
};

export type UpdateEmailReachContactResponse =
  paths['/emailreach/contact/{account_id}/{contact_id}/']['put']['responses'][200]['content']['application/json'];

export type GetEmailReachContactByIdParams = {
  account_id?: string;
  contact_id?: string;
};

export type ExtendedEmailReachByIdItem = {
  first_name: string;
  last_name: string;
  email_address: string;
  create_source?: string;
  contact_id?: string | undefined;
  lists?: string[] | undefined;
  status?: 'active' | 'unsubscribed';
  [key: string]: any;
};

export type GetEmailReachContactByIdResponse = {
  status?: CommonResultType['status'];
  data?: ExtendedEmailReachByIdItem;
  [key: string]: any;
};

export type DeleteEmailReachContactArgs = {
  account_id?: string;
  contact_id?: string;
};

export type DeleteEmailReachContactResponse = StatusOnlyResponse;

// Email Reach Contact List types

export type EmailReachContactListById = {
  uuid?: string;
  account_id?: string;
  list_id?: string;
  site_id?: string;
  external_id?: string;
  title?: string;
  created_at?: string;
  updated_at?: string;
  priority?: number;
  icon?: string;
  frequency?: string;
  description?: string;
  anonymous?: boolean;
  display_on_signup?: boolean;
  hide?: boolean;
  from_name?: string;
  from_email?: string;
};

export type GetEmailReachContactListByIdParams = {
  account_id?: string;
  list_id?: string;
};

export type GetEmailReachContactListByIdResponse = {
  status?: CommonResultType['status'];
  data?: EmailReachContactListById;
};

export type UpdateEmailReachContactListPayload = {
  title?: string;
  priority?: number;
  icon?: string;
  frequency?: string;
  description?: string;
  anonymous?: boolean;
  display_on_signup?: boolean;
  hide?: boolean;
  from_name?: string;
  from_email?: string;
};

export type UpdateEmailReachContactListParams = {
  account_id?: string;
  list_id?: string;
  payload?: UpdateEmailReachContactListPayload;
};

export type UpdateEmailReachContactListResponse = {
  status?: CommonResultType['status'];
  data?: EmailReachContactListById;
};

export type CreateEmailReachContactListParams = {
  account_id?: string;
  payload?: UpdateEmailReachContactListPayload;
};

export type CreateEmailReachContactListResponse = {
  status?: CommonResultType['status'];
  data?: EmailReachContactListById;
};

export type DeleteEmailReachContactListParams = {
  account_id?: string;
  list_id?: string;
};

export type DeleteEmailReachContactListResponse = StatusOnlyResponse;

type BaseEmailReachCampaignSearchResults =
  components['schemas']['emailreach.campaign.search_results'];

type BaseEmailReachCampaign = components['schemas']['emailreach.campaign.campaign'];

type BaseEmailReachCampaignUpdate =
  components['schemas']['emailreach.campaign.campaign-update'];

type BaseEmailReachCampaignSingleRun =
  components['schemas']['emailreach.campaign.single_run'];

type BaseEmailReachSegmentSearchResults =
  components['schemas']['emailreach.segment.search_results'];

export type GetEmailReachCampaignsParams = {
  account_id?: string;
  page?: string | number;
  limit?: string | number;
  list_id?: string;
  segment_id?: string;
};

export type GetEmailReachCampaignsResponse = {
  status?: CommonResultType['status'];
  data?: BaseEmailReachCampaignSearchResults;
  [key: string]: any;
};

export type CreateEmailReachCampaignPayload = BaseEmailReachCampaign;

export type CreateEmailReachCampaignResponse = {
  status?: CommonResultType['status'];
  data?: BaseEmailReachCampaign;
};

export type UpdateEmailReachCampaignPayload = BaseEmailReachCampaignUpdate;

export type UpdateEmailReachCampaignResponse = {
  status?: CommonResultType['status'];
  data?: BaseEmailReachCampaignUpdate;
};

export type GetEmailReachCampaignByIdResponse = {
  status?: CommonResultType['status'];
  data?: BaseEmailReachCampaign;
};

export type TriggerEmailReachCampaignRunPayload = BaseEmailReachCampaignSingleRun;

export type TriggerEmailReachCampaignRunResponse = {
  status?: CommonResultType['status'];
  data?: BaseEmailReachCampaignSingleRun;
};

export type GetEmailReachSegmentsParams = {
  account_id?: string;
  page?: string | number;
  limit?: string | number;
};

export type GetEmailReachSegmentsResponse = {
  status?: CommonResultType['status'];
  data?: BaseEmailReachSegmentSearchResults;
  [key: string]: any;
};

export type EmailReachMessageItem = {
  uuid: string;
  campaign_id: string;
  account_id: string;
  external_id: string;
  email_subject: string;
  title: string;
  status: string;
  sent_date: string;
};

export type GetEmailReachMessagesParams = {
  account_id?: string;
  page?: string | number;
  limit?: string | number;
  search?: string;
  list_type?: 'list' | 'segment' | (string & {});
  campaign_type?: 'scheduled' | 'single_run' | (string & {});
  ids?: string[];
};

export type GetEmailReachMessagesResult = {
  status?: CommonResultType['status'];
  data?: {
    total?: number;
    next?: string | null;
    prev?: string | null;
    facets?: Record<string, Record<string, number>>;
    items?: Array<{
      uuid: string;
      campaign_id: string;
      account_id: string;
      external_id: string;
      email_subject: string;
      title: string;
      status: string;
      sent_date: string;
    }>;
  };
};

export type GetEmailReachMessageByIdParams = {
  account_id?: string;
  message_id?: string;
};

export type GetEmailReachMessageByIdResponse = {
  status?: CommonResultType['status'];
  data?: {
    uuid: string;
    campaign_id: string;
    account_id: string;
    external_id: string;
    email_subject: string;
    title: string;
    status: string;
    sent_date: string;
    report_data?: {
      bounces: number;
      clicks: number;
      forwards: number;
      opens: number;
      sends: number;
      spam: number;
      links: { link_url: string; unique_clicks: number }[];
    };
  };
};

// ------------------------------------------------
// EmailReach Provider
// ------------------------------------------------

export const emailReachProvider = {
  /**
   * POST /emailreach/account/
   * Creates or updates an EmailReach account.
   *
   * @param payload EmailReachAccountPayload
   * @returns An object containing the API response status and data.
   */
  createEmailReachAccount: async ({
    payload,
  }: CreateEmailReachAccountParams): Promise<CreateEmailReachAccountResponse> => {
    try {
      if (!payload) {
        throw new Error('Missing required payload.');
      }

      const path = `/emailreach/account/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(payload),
      });

      const responseData = data?.data;
      const normalizedItems = responseData?.items?.map((item: EmailReachMessageItem) =>
        item?.status
          ? {
              ...item,
              status: toTitleCase(item.status),
            }
          : item,
      );

      return {
        status: data?.status ?? status,
        data: responseData
          ? {
              ...responseData,
              ...(normalizedItems ? { items: normalizedItems } : {}),
            }
          : responseData,
      };
    } catch (error) {
      console.error('[createEmailReachAccount] Error:', error);
      throw error;
    }
  },

  /**
   * POST /emailreach/account/{account_id}/reauthenticate/
   * Reauthenticate an EmailReach account to refresh vendor credentials.
   */
  reauthenticateEmailReachAccount: async ({
    account_id,
    payload,
  }: ReauthenticateEmailReachAccountParams): Promise<ReauthenticateEmailReachAccountResponse> => {
    try {
      if (!account_id || !payload?.vendor_auth_data?.oauth_code) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/account/${account_id}/reauthenticate/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(payload),
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[reauthenticateEmailReachAccount] Error:', error);
      throw error;
    }
  },

  /**
   * PUT /emailreach/account/
   * Creates or updates an EmailReach account.
   *
   * @param payload EmailReachAccountPayload
   * @returns An object containing the API response status and data.
   */
  updateEmailReachAccount: async ({
    id,
    payload,
  }: UpdateEmailReachAccountParams): Promise<UpdateEmailReachAccountResponse> => {
    try {
      if (!id || !payload) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/account/${id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(payload),
      });

      const messageData = data?.data;
      const normalizedMessage = messageData?.status
        ? { ...messageData, status: toTitleCase(messageData.status) }
        : messageData;

      return {
        status: data?.status ?? status,
        data: normalizedMessage,
      };
    } catch (error) {
      console.error('[updateEmailReachAccount] Error:', error);
      throw error;
    }
  },

  /**
   * GET /emailreach/account/{account_id}/
   * Retrieves an EmailReach account by its ID.
   *
   * @param accountId The unique identifier for the EmailReach account.
   * @returns An object containing the API response status and data.
   */
  getEmailReachAccountById: async ({
    accountId,
  }: GetEmailReachAccountByIdParams): Promise<GetEmailReachAccountByIdResult> => {
    try {
      const path = `/emailreach/account/${accountId}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getEmailReachAccountById] Error:', error);
      throw error;
    }
  },

  /**
   * GET /emailreach/account/
   * Retrieves a list of EmailReach accounts.
   *
   * @param page Token for the page to return
   * @param limit Number of items to return per page
   * @returns An object containing the API response status and data.
   */
  getEmailReachAccounts: async ({
    page,
    limit,
  }: GetEmailReachAccountsParams): Promise<GetEmailReachAccountsResult> => {
    try {
      const path = `/emailreach/account/`;

      const payload = {
        ...(page && { page: page }),
        ...(limit && { limit: limit }),
      };

      const qpStr = Object.keys(payload).length
        ? `?${qs.stringify(payload, qsOptions)}`
        : '';

      const finalPath = `${path}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'GET',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getEmailReachAccounts] Error:', error);
      throw error;
    }
  },

  /**
   * DELETE /emailreach/account/{account_id}/
   * Deletes an EmailReach account by its ID.
   *
   * @param accountId The unique identifier for the EmailReach account.
   * @returns 204 No Content if successful, or an error message if failed.
   */
  deleteEmailReachAccount: async ({
    accountId,
  }: DeleteEmailReachAccountParams): Promise<DeleteEmailReachAccountResponse> => {
    try {
      if (!accountId) {
        throw new Error('Missing required parameter.');
      }

      const path = `/emailreach/account/${accountId}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[deleteEmailReachAccount] Error:', error);
      throw error;
    }
  },

  /**
   * GET /emailreach/vendor/
   * Retrieves a list of supported EmailReach vendors.
   *
   * @param page Token for the page to return
   * @param limit Number of items to return per page
   * @returns An object containing the API response status and data.
   */
  getEmailReachVendors: async ({
    page,
    limit,
  }: GetEmailReachVendorsParams): Promise<GetEmailReachVendorsResponse> => {
    try {
      const path = `/emailreach/vendor/`;

      const payload = {
        ...(page && { page: page }),
        ...(limit && { limit: limit }),
      };

      const qpStr = Object.keys(payload).length
        ? `?${qs.stringify(payload, qsOptions)}`
        : '';

      const finalPath = `${path}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'GET',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getEmailReachVendors] Error:', error);
      throw error;
    }
  },

  /**
   * POST /emailreach/contact/{account_id}/
   * Creates a new EmailReach contact.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param payload EmailReachContactPayload
   * @returns An object containing the API response status and data.
   */
  createEmailReachContact: async ({
    account_id,
    payload,
  }: CreateEmailReachContactParams): Promise<CreateEmailReachContactResponse> => {
    try {
      if (!account_id || !payload) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contact/${account_id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(payload),
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[createEmailReachContact] Error:', error);
      throw error;
    }
  },

  /**
   * GET /emailreach/contactlist/{account_id}/
   * Retrieves a list of contact lists for a specific EmailReach account.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param page Token for the page to return
   * @param limit Number of items to return per page
   * @returns An object containing the API response status and data.
   */
  getEmailReachContactLists: async (
    accountOrParams: string | GetEmailReachContactListParams,
    pageArg?: GetEmailReachContactListParams['page'],
    limitArg?: GetEmailReachContactListParams['limit'],
  ): Promise<GetEmailReachContactListResponse> => {
    try {
      const { account_id, page, limit } =
        typeof accountOrParams === 'string'
          ? {
              account_id: accountOrParams,
              page: pageArg,
              limit: limitArg,
            }
          : {
              account_id: accountOrParams.account_id,
              page: accountOrParams.page,
              limit: accountOrParams.limit,
            };

      if (!account_id) {
        throw new Error('Missing required parameter.');
      }

      const path = `/emailreach/contactlist/${account_id}/`;

      const query: Record<string, string | number> = {};

      if (page !== undefined && page !== null && page !== '') {
        query.page = page;
      }

      if (limit !== undefined && limit !== null && limit !== '') {
        query.limit = limit;
      }

      const qpStr = Object.keys(query).length
        ? `?${qs.stringify(query, qsOptions)}`
        : '';
      const finalPath = `${path}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'GET',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getEmailReachContactLists] Error:', error);
      throw error;
    }
  },

  /**
   * GET /emailreach/segment/{account_id}/
   * Retrieves a list of segments for a specific EmailReach account.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param page Token for the page to return
   * @param limit Number of items to return per page
   * @returns An object containing the API response status and data.
   */
  getEmailReachSegments: async (
    accountOrParams: string | GetEmailReachSegmentsParams,
    pageArg?: GetEmailReachSegmentsParams['page'],
    limitArg?: GetEmailReachSegmentsParams['limit'],
  ): Promise<GetEmailReachSegmentsResponse> => {
    try {
      const { account_id, page, limit } =
        typeof accountOrParams === 'string'
          ? {
              account_id: accountOrParams,
              page: pageArg,
              limit: limitArg,
            }
          : {
              account_id: accountOrParams.account_id,
              page: accountOrParams.page,
              limit: accountOrParams.limit,
            };

      if (!account_id) {
        throw new Error('Missing required parameter.');
      }

      const path = `/emailreach/segment/${account_id}/`;

      const query: Record<string, string | number> = {};

      if (page !== undefined && page !== null && page !== '') {
        query.page = page;
      }

      if (limit !== undefined && limit !== null && limit !== '') {
        query.limit = limit;
      }

      const qpStr = Object.keys(query).length
        ? `?${qs.stringify(query, qsOptions)}`
        : '';
      const finalPath = `${path}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'GET',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getEmailReachSegments] Error:', error);
      throw error;
    }
  },

  /**
   * GET /emailreach/campaign/{account_id}/
   * Retrieves a list of EmailReach campaigns for a specific account.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param page Token for the page to return
   * @param limit Number of items to return per page
   * @param list_id Filter by list identifier
   * @param segment_id Filter by segment identifier
   * @returns An object containing the API response status and data.
   */
  getEmailReachCampaigns: async (
    account_id: GetEmailReachCampaignsParams['account_id'],
    page?: GetEmailReachCampaignsParams['page'],
    limit?: GetEmailReachCampaignsParams['limit'],
    list_id?: GetEmailReachCampaignsParams['list_id'],
    segment_id?: GetEmailReachCampaignsParams['segment_id'],
  ): Promise<GetEmailReachCampaignsResponse> => {
    try {
      if (!account_id) {
        throw new Error('Missing required parameter.');
      }

      const path = `/emailreach/campaign/${account_id}/`;

      const query: Record<string, string | number> = {};

      if (page !== undefined && page !== null && page !== '') {
        query.page = page;
      }

      if (limit !== undefined && limit !== null && limit !== '') {
        query.limit = limit;
      }

      if (list_id) {
        query.list_id = list_id;
      }

      if (segment_id) {
        query.segment_id = segment_id;
      }

      const qpStr = Object.keys(query).length
        ? `?${qs.stringify(query, qsOptions)}`
        : '';
      const finalPath = `${path}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'GET',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getEmailReachCampaigns] Error:', error);
      throw error;
    }
  },

  /**
   * GET /emailreach/contactlist/{account_id}/{list_id}/
   * Retrieves a specific contact list by its ID for an EmailReach account.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param list_id The unique identifier for the contact list.
   * @returns An object containing the API response status and data.
   */
  getEmailReachContactListById: async (
    accountOrParams: string | GetEmailReachContactListByIdParams,
    maybeListId?: string | number,
  ): Promise<GetEmailReachContactListByIdResponse> => {
    try {
      const accountId =
        typeof accountOrParams === 'string'
          ? accountOrParams
          : accountOrParams.account_id;
      const listId =
        typeof accountOrParams === 'string'
          ? maybeListId !== undefined && maybeListId !== null
            ? String(maybeListId)
            : undefined
          : accountOrParams.list_id;

      if (!accountId || !listId) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contactlist/${accountId}/${listId}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getEmailReachContactListById] Error:', error);
      throw error;
    }
  },

  /**
   * POST /emailreach/contactlist/{account_id}/
   * Creates a new EmailReach contact list.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param payload UpdateEmailReachContactListPayload
   * @returns An object containing the API response status and data.
   */
  createEmailReachContactList: async ({
    account_id,
    payload,
  }: CreateEmailReachContactListParams): Promise<CreateEmailReachContactListResponse> => {
    try {
      if (!account_id || !payload) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contactlist/${account_id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(payload),
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[createEmailReachContactList] Error:', error);
      throw error;
    }
  },

  /**
   * POST /emailreach/campaign/{account_id}/
   * Creates a new EmailReach campaign for a specific account.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param payload The campaign data to create.
   *
   * @returns An object containing the API response status and data.
   */
  createEmailReachCampaign: async (
    account_id: string,
    payload: CreateEmailReachCampaignPayload,
  ): Promise<CreateEmailReachCampaignResponse> => {
    try {
      if (!account_id || !payload) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/campaign/${account_id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(payload),
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[createEmailReachCampaign] Error:', error);
      throw error;
    }
  },

  /**
   * PUT /emailreach/campaign/{account_id}/{campaign_id}/
   * Updates an existing EmailReach campaign for a specific account.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param campaign_id The campaign identifier.
   * @param payload The campaign data to update.
   *
   * @returns An object containing the API response status and data.
   */
  updateEmailReachCampaign: async (
    account_id: string,
    campaign_id: string,
    payload: UpdateEmailReachCampaignPayload,
  ): Promise<UpdateEmailReachCampaignResponse> => {
    try {
      if (!account_id || !campaign_id || !payload) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/campaign/${account_id}/${campaign_id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(payload),
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[updateEmailReachCampaign] Error:', error);
      throw error;
    }
  },

  /**
   * GET /emailreach/campaign/{account_id}/{campaign_id}/
   * Retrieves a specific EmailReach campaign by ID for an account.
   *
   * @param account_id The EmailReach account identifier.
   * @param campaign_id The campaign identifier.
   */
  getEmailReachCampaignById: async (
    account_id: string,
    campaign_id: string,
  ): Promise<GetEmailReachCampaignByIdResponse> => {
    try {
      if (!account_id || !campaign_id) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/campaign/${account_id}/${campaign_id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getEmailReachCampaignById] Error:', error);
      throw error;
    }
  },

  triggerEmailReachCampaignRun: async (
    account_id: string,
    id: string,
    payload?: TriggerEmailReachCampaignRunPayload,
  ): Promise<TriggerEmailReachCampaignRunResponse> => {
    try {
      if (!account_id || !id) {
        throw new Error('[triggerEmailReachCampaignRun] Missing account_id or id');
      }

      const path = `/emailreach/campaign/${account_id}/single_run/${id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: payload ? JSON.stringify(payload) : undefined,
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[triggerEmailReachCampaignRun] Error:', error);
      throw error;
    }
  },
  /**
   * PUT /emailreach/contactlist/{account_id}/{list_id}/
   * Updates an existing EmailReach contact list.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param list_id The unique identifier for the contact list.
   * @param payload UpdateEmailReachContactListPayload
   * @returns An object containing the API response status and data.
   */
  updateEmailReachContactList: async ({
    account_id,
    list_id,
    payload,
  }: UpdateEmailReachContactListParams): Promise<UpdateEmailReachContactListResponse> => {
    try {
      if (!account_id || !list_id || !payload) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contactlist/${account_id}/${list_id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(payload),
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[updateEmailReachContactList] Error:', error);
      throw error;
    }
  },

  /**
   * DELETE /emailreach/contactlist/{account_id}/{list_id}/
   * Deletes an EmailReach contact list by its ID.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param list_id The unique identifier for the contact list.
   * @returns 204 No Content if successful, or an error message if failed.
   */
  deleteEmailReachContactList: async ({
    account_id,
    list_id,
  }: DeleteEmailReachContactListParams): Promise<DeleteEmailReachContactListResponse> => {
    try {
      if (!account_id || !list_id) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contactlist/${account_id}/${list_id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[deleteEmailReachContactList] Error:', error);
      throw error;
    }
  },

  /**
   * POST /emailreach/contactlist/{account_id}/{list_id}/export
   * Creates a background task to export all contacts for a given list.
   */
  exportEmailReachContactList: async ({
    account_id,
    list_id,
  }: EmailReachContactListExportParams): Promise<EmailReachContactListExportResponse> => {
    try {
      if (!account_id || !list_id) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contactlist/${account_id}/${list_id}/export/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[exportEmailReachContactList] Error:', error);
      throw error;
    }
  },

  /**
   * GET /emailreach/contactlist/{account_id}/export/download/{id}
   * Polls for the export file and returns the CSV blob when ready.
   */
  downloadEmailReachContactListExport: async ({
    account_id,
    export_id,
  }: EmailReachContactListExportDownloadParams): Promise<EmailReachContactListExportDownloadResponse> => {
    try {
      if (!account_id || !export_id) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contactlist/${account_id}/export/download/${export_id}/`;

      const response = await httpClient<Blob>({
        url: path,
        method: 'GET',
        responseType: 'blob',
      });

      return response;
    } catch (error) {
      console.error('[downloadEmailReachContactListExport] Error:', error);
      throw error;
    }
  },

  getEmailReachContactListExportStatus: async ({
    account_id,
    export_id,
  }: EmailReachContactListExportDownloadParams): Promise<EmailReachContactListExportStatusResponse> => {
    try {
      if (!account_id || !export_id) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contactlist/${account_id}/export/download/${export_id}/`;
      const response = await httpClient({
        url: path,
        method: 'HEAD',
      });

      return {
        status: response.status,
      };
    } catch (error) {
      console.error('[getEmailReachContactListExportStatus] Error:', error);
      throw error;
    }
  },

  /**
   * PUT /emailreach/contact/{account_id}/{contact_id}/
   * Updates an existing EmailReach contact.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param contact_id The unique identifier for the EmailReach contact.
   * @param payload EmailReachEditContactPayload
   * @returns An object containing the API response status and data.
   */
  updateEmailReachContact: async ({
    account_id,
    contact_id,
    payload,
  }: UpdateEmailReachContactParams): Promise<UpdateEmailReachContactResponse> => {
    try {
      if (!account_id || !contact_id || !payload) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contact/${account_id}/${contact_id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(payload),
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[updateEmailReachContact] Error:', error);
      throw error;
    }
  },

  /**
   * GET /emailreach/contact/{account_id}/{contact_id}/
   * Retrieves an EmailReach contact by its ID.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param contact_id The unique identifier for the EmailReach contact.
   * @returns An object containing the API response status and data.
   */
  getEmailReachContactById: async ({
    account_id,
    contact_id,
  }: GetEmailReachContactByIdParams): Promise<GetEmailReachContactByIdResponse> => {
    try {
      if (!account_id || !contact_id) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contact/${account_id}/${contact_id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });
      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getEmailReachContactById] Error:', error);
      throw error;
    }
  },

  /**
   * DELETE /emailreach/contact/{account_id}/{contact_id}/
   * Deletes an EmailReach contact by its ID.
   *
   * @param account_id The unique identifier for the EmailReach account.
   * @param contact_id The unique identifier for the EmailReach contact.
   * @returns An object containing the API response status and data.
   */
  deleteEmailReachContact: async ({
    account_id,
    contact_id,
  }: DeleteEmailReachContactArgs): Promise<DeleteEmailReachContactResponse> => {
    try {
      if (!account_id || !contact_id) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contact/${account_id}/${contact_id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });
      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[deleteEmailReachContact] Error:', error);
      throw error;
    }
  },

  /**
   * PUT /emailreach/contact/{account_id}/{contact_id}/subscribe/
   * Sets the contact's global status to subscribed with the provided list membership.
   */
  subscribeEmailReachContact: async ({
    account_id,
    contact_id,
    payload,
  }: SubscribeEmailReachContactParams): Promise<SubscribeEmailReachContactResponse> => {
    try {
      if (!account_id || !contact_id || !payload) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contact/${account_id}/${contact_id}/subscribe/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(payload),
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[subscribeEmailReachContact] Error:', error);
      throw error;
    }
  },

  /**
   * PUT /emailreach/contact/{account_id}/{contact_id}/unsubscribe/
   * Sets the contact's global status to unsubscribed.
   */
  unsubscribeEmailReachContact: async ({
    account_id,
    contact_id,
  }: UnsubscribeEmailReachContactParams): Promise<UnsubscribeEmailReachContactResponse> => {
    try {
      if (!account_id || !contact_id) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contact/${account_id}/${contact_id}/unsubscribe/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[unsubscribeEmailReachContact] Error:', error);
      throw error;
    }
  },

  /**
   * PUT /emailreach/contact/{account_id}/{contact_id}/send-opt-in-email
   * Sends an opt-in email to the contact.
   */
  sendOptInEmail: async ({
    account_id,
    contact_id,
    list_ids,
  }: SendOptInEmailParams): Promise<UnsubscribeEmailReachContactResponse> => {
    try {
      if (!account_id || !contact_id) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/contact/${account_id}/${contact_id}/send-opt-in-email/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: { list_ids: list_ids ?? [] },
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[sendOptInEmail] Error:', error);
      throw error;
    }
  },

  getEmailReachMessages: async ({
    account_id,
    page,
    limit,
    search,
    list_type,
    campaign_type,
    ids,
  }: GetEmailReachMessagesParams): Promise<GetEmailReachMessagesResult> => {
    try {
      if (!account_id) {
        throw new Error('Missing required parameter.');
      }

      const path = `/emailreach/messages/${account_id}/`;

      const payload: Record<string, string | number | string[]> = {
        ...(page !== undefined && page !== null && page !== '' ? { page } : {}),
        ...(limit !== undefined && limit !== null && limit !== '' ? { limit } : {}),
        ...(search ? { search } : {}),
        ...(list_type ? { list_type } : {}),
        ...(campaign_type ? { campaign_type } : {}),
        ...(ids && ids.length > 0 ? { ids } : {}),
      };

      const qpStr = Object.keys(payload).length
        ? `?${qs.stringify(payload, qsOptions)}`
        : '';
      const finalPath = `${path}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'GET',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getEmailReachMessages] Error:', error);
      throw error;
    }
  },

  getEmailReachMessageById: async ({
    account_id,
    message_id,
  }: GetEmailReachMessageByIdParams): Promise<GetEmailReachMessageByIdResponse> => {
    try {
      if (!account_id || !message_id) {
        throw new Error('Missing required parameters.');
      }

      const path = `/emailreach/messages/${account_id}/${message_id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status: data?.status ?? status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getEmailReachMessageById] Error:', error);
      throw error;
    }
  },
} as const;

export type EmailReachProviderType = typeof emailReachProvider;
